import type { RouteMap, PlaybackRoute, RouteWithAction } from '@/types'
import { MAX_WAIT_SEC } from '@/config/scenarioConfig'

// ===============================
// 停止手順（wait / wait_resume）
// ===============================
// シナリオの routes[] には記録ルートのほかに「停止手順」を置ける。
// 停止手順は直前ルートの終点スロットでその場に停止する手順で、
// mapId=''、slots=[停止位置]、startSlot=endSlot=停止位置 で表す
// （startSlot/endSlot を持つことで、後続ルートの接続判定はそのまま通る）。
// - action='wait'        : waitSec 秒停止して次へ進む（機体の wait タスク）
// - action='wait_resume' : タッチパネルのタップ（resume コマンド）まで停止する
//                          （機体の wait_resume タスク。TapToStart）
// fork_webapp_react の routeUtil と同じ規則。

// 停止手順（秒数指定・タップで再開の両方）か。記録ルートではない手順の判定に使う
export function isWaitStep(route: PlaybackRoute | null | undefined): boolean {
  return route?.action === 'wait' || route?.action === 'wait_resume'
}

// 秒数指定の停止手順か
export function isTimedWaitStep(
  route: PlaybackRoute | null | undefined
): boolean {
  return route?.action === 'wait'
}

// タップで再開する一時停止手順か
export function isResumeWaitStep(
  route: PlaybackRoute | null | undefined
): boolean {
  return route?.action === 'wait_resume'
}

// 停止秒数を正規化する（0〜MAX の整数秒。不正値は 0）。0 は未入力扱いで保存不可
export function normalizeWaitSec(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return 0
  return Math.min(Math.max(Math.floor(n), 0), MAX_WAIT_SEC)
}

// 停止手順を生成する（slotId = 停止位置。直前ルートの終点スロット）。
// シナリオ先頭の停止は位置を持たないので undefined（slots=[]、startSlot/endSlot なし。
// 出発位置で停止する）
export function createWaitStep(
  slotId: string | undefined,
  waitSec: number,
  id: string = `wait-${Date.now()}`
): RouteWithAction {
  return {
    id,
    mapId: '',
    slots: slotId ? [slotId] : [],
    action: 'wait',
    isSwapped: false,
    startSlot: slotId,
    endSlot: slotId,
    waitSec: normalizeWaitSec(waitSec),
    computedRoutePoints: null,
  }
}

// 一時停止手順（タップで再開）を生成する。位置の扱いは createWaitStep と同じ
export function createWaitResumeStep(
  slotId: string | undefined,
  id: string = `wait_resume-${Date.now()}`
): RouteWithAction {
  return {
    id,
    mapId: '',
    slots: slotId ? [slotId] : [],
    action: 'wait_resume',
    isSwapped: false,
    startSlot: slotId,
    endSlot: slotId,
    computedRoutePoints: null,
  }
}

// 末尾から見て最初に位置（終点スロット）を持つ手順の終点を返す。
// 先頭の停止手順しか無い場合など、位置を持つ手順が無ければ undefined
export function lastPositionedEndSlot(
  routes: ReadonlyArray<PlaybackRoute>
): string | undefined {
  for (let i = routes.length - 1; i >= 0; i--) {
    const endSlot =
      routes[i].endSlot || routes[i].slots[1] || routes[i].slots[0]
    if (endSlot) return endSlot
  }
  return undefined
}

// 末尾から見て最初の記録ルート（停止手順を除く）を返す
export function lastRouteStep<T extends PlaybackRoute>(
  routes: ReadonlyArray<T>
): T | null {
  for (let i = routes.length - 1; i >= 0; i--) {
    if (!isWaitStep(routes[i])) return routes[i]
  }
  return null
}

export function formatMapId(mapId: string): string {
  const match = mapId.match(/^(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})$/)
  if (!match) return mapId
  const [, year, month, day, hour, minute, second] = match
  return `${year}-${month}-${day} ${hour}:${minute}:${second}`
}

// ISO8601 文字列を "YYYY-MM-DD HH:mm" 形式へ整形する（dayjs 非依存）。
export function formatDateTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(
    d.getHours()
  )}:${p(d.getMinutes())}`
}

// スロット ID からスロット位置（ステーションの座標）を取得する。
export function getSlotPosition(map: RouteMap | null, slotId: string) {
  if (!map || !map.slots || !map.stations) return null
  const slot = map.slots.get(slotId)
  if (!slot) return null
  const station = map.stations.get(slot.stationId)
  if (!station || !station.pose) return null
  return { x: station.pose.x, y: station.pose.y }
}

// L1 距離（マンハッタン距離）を計算する。
export function calcL1Distance(
  pos1: { x: number; y: number } | null,
  pos2: { x: number; y: number } | null
) {
  if (!pos1 || !pos2) return Infinity
  const dx = pos1.x - pos2.x
  const dy = pos1.y - pos2.y
  return Math.abs(dx) + Math.abs(dy)
}

// スロット ID からラベル（rec-1 など）を抽出する。
export function getSlotLabel(slotId: string) {
  const match = slotId.match(/\/(rec-\d+)$/)
  return match ? match[1] : slotId
}

// 各ルートのパスを荷物を持って走行するかどうかを判定する。
// - 荷積み(pickup)完了後〜荷下ろし(place)完了までが荷あり区間
//   （先頭が荷下ろしの場合など、荷積みを経ない荷下ろしも荷ありで走行する）
//   （pickupルート自体は荷なしで走行し、placeルートは荷ありで走行する）
// - move_loaded（荷物あり移動）は常に荷あり
export function computeLoadedFlags(
  routes: ReadonlyArray<PlaybackRoute>
): boolean[] {
  let carrying = false
  return routes.map((route) => {
    const action = (route as RouteWithAction).action || 'move'
    if (action === 'move_loaded') carrying = true
    // 荷下ろしは直前に荷積みが無くても（先頭ルート等）荷物を持って走行する
    const loaded =
      action === 'pickup' ? false : action === 'place' ? true : carrying
    if (action === 'pickup') carrying = true
    if (action === 'place') carrying = false
    return loaded
  })
}

// PlaybackRoute を編集可能な RouteWithAction に変換する（既定値を補完）。
export function convertToRouteWithAction(
  route: PlaybackRoute
): RouteWithAction {
  const routeWithAction = route as RouteWithAction
  return {
    ...routeWithAction,
    action: routeWithAction.action || 'move',
    isSwapped: routeWithAction.isSwapped || false,
    startSlot: routeWithAction.startSlot || route.slots[0],
    endSlot: routeWithAction.endSlot || route.slots[1],
  }
}
